/**
 * Como o checkout mostra um cupom de PLATAFORMA numa assinatura.
 *
 * Quem decide o valor cobrado é o pay (na contratação e a cada ciclo); isto é
 * só a conta que o comprador vê antes de pagar — a mesma regra, para a tela não
 * prometer outra coisa.
 */
import { ICoupon } from '../api/coupons/interfaces/couponInterface';

const toCents = (value: number) => Math.round(Number(value) * 100);

/** Desconto em reais do cupom sobre `price` (nunca passa do preço). */
export const couponDiscountFor = (
	price: number,
	coupon?: Pick<
		ICoupon,
		'discountType' | 'discountValue' | 'flatDiscount'
	> | null
): number => {
	if (!coupon) return 0;
	const priceCents = toCents(price);
	if (!Number.isFinite(priceCents) || priceCents <= 0) return 0;

	const raw =
		coupon.discountType === 'PERCENT'
			? Math.round((priceCents * Number(coupon.discountValue)) / 100)
			: toCents(coupon.discountValue ?? coupon.flatDiscount ?? 0);

	if (!Number.isFinite(raw) || raw <= 0) return 0;
	return Math.min(raw, priceCents) / 100;
};

/**
 * Preço do plano com o cupom — o valor do ciclo que o pay cobra e a base do
 * parcelamento. Resumo e parcelas usam esta MESMA conta.
 */
export const planPriceWithCoupon = (
	price: number,
	coupon?: Pick<
		ICoupon,
		'discountType' | 'discountValue' | 'flatDiscount'
	> | null
): number => {
	const value = Number(price) || 0;
	return Math.round((value - couponDiscountFor(value, coupon)) * 100) / 100;
};

/** "na 1ª cobrança" · "nas 3 primeiras cobranças" · "em todas as cobranças" */
export const couponDurationLabel = (
	coupon?: Pick<ICoupon, 'duration' | 'durationCycles'> | null
): string => {
	if (!coupon?.duration || coupon.duration === 'ONCE')
		return 'na 1ª cobrança';
	if (coupon.duration === 'FOREVER') return 'em todas as cobranças';

	const cycles = Number(coupon.durationCycles) || 1;
	return cycles === 1
		? 'na 1ª cobrança'
		: `nas ${cycles} primeiras cobranças`;
};

/** O desconto acaba em algum momento? (para mostrar o "depois, R$ X"). */
export const couponEnds = (
	coupon?: Pick<ICoupon, 'duration'> | null
): boolean => !!coupon && coupon.duration !== 'FOREVER';
